/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#processExternalLinks(this);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Processes external links to add target="_blank"
   * @param {HTMLElement} container - The container element to search for links
   */
  #processExternalLinks(container) {
    const links = container.querySelectorAll('a[href]');
    const currentHost = window.location.hostname;

    links.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;

      try {
        const url = new URL(href, window.location.origin);
        // Check if link is external (different hostname) and not already has target="_blank"
        if (url.hostname !== currentHost && url.hostname !== '') {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
        }
      } catch (e) {
        // If URL parsing fails, check if it starts with http:// or https://
        if (href.startsWith('http://') || href.startsWith('https://')) {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
        }
      }
    });
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}

/**
 * Process external links in .rte containers that aren't wrapped in rte-formatter
 */
function processRteExternalLinks() {
  const rteContainers = document.querySelectorAll('.rte:not(rte-formatter)');
  const currentHost = window.location.hostname;

  rteContainers.forEach((container) => {
    const links = container.querySelectorAll('a[href]');
    links.forEach((link) => {
      // Skip if already processed or already has target="_blank"
      if (link.dataset.externalProcessed || link.getAttribute('target') === '_blank') {
        return;
      }

      const href = link.getAttribute('href');
      if (!href) return;

      try {
        const url = new URL(href, window.location.origin);
        // Check if link is external (different hostname) and not already has target="_blank"
        if (url.hostname !== currentHost && url.hostname !== '') {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
          link.dataset.externalProcessed = 'true';
        }
      } catch (e) {
        // If URL parsing fails, check if it starts with http:// or https://
        if (href.startsWith('http://') || href.startsWith('https://')) {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
          link.dataset.externalProcessed = 'true';
        }
      }
    });
  });
}

// Process links on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', processRteExternalLinks);
} else {
  processRteExternalLinks();
}

// Also process links when new content is added (for dynamic content)
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    let shouldProcess = false;
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Element node
            if (node.classList?.contains('rte') || node.querySelector?.('.rte')) {
              shouldProcess = true;
            }
          }
        });
      }
    });
    if (shouldProcess) {
      processRteExternalLinks();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
